/**
 * @description Component-LiquidityHistory
 */
import './index.less';

import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useInfiniteLiquidityHistory } from '@/features/graph/hooks';
import { LiquidityHistory as LiquidityHistoryType } from '@/features/graph/types';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useChainId, useEtherscanLink, useUserAddr } from '@/hooks/web3/useChain';

import Empty from '@/components/Empty';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { ExternalLink } from '@/components/Link';
import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import { formatDate } from '@/utils/timeUtils';
import { mappingToken } from '@/utils/token';
import MobileHistoryCard from '../MobileHistoryCard';
import MobileHistorySkeleton from '../MobileHistoryCard/Skeleton';

interface IPropTypes {
  isTradePage?: boolean;
}

const LiquidityHistory: FC<IPropTypes> = ({ isTradePage = false }) => {
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { t } = useTranslation();
  const currentPair = useCurrentPairFromUrl(chainId);
  const pair = isTradePage ? currentPair : 'all';
  const getEtherscanLink = useEtherscanLink();

  const {
    histories,
    queryState: { isLoading, isFetching },
  } = useInfiniteLiquidityHistory({ chainId, userAddr, pair });

  const loading = isLoading || isFetching;

  const renderCard = (record: LiquidityHistoryType) => {
    const priceMin = WrappedBigNumber.from(record.lowerPrice);
    const priceMax = WrappedBigNumber.from(record.upperPrice);

    return (
      <MobileHistoryCard
        key={record.id}
        header={
          <>
            <div className="value">
              <EmptyDataWrap isLoading={!record?.instrumentAddress}>
                {chainId && (
                  <TokenPairSimple
                    // tradeSide={record.side ? (record.side === 'long' ? Side.LONG : Side.SHORT) : Side.FLAT}
                    // isInverse={record.isInverse}
                    baseToken={mappingToken({
                      token: record.baseToken,
                      chainId: chainId,
                    })}
                    quoteToken={mappingToken({
                      token: record.quoteToken,
                      chainId: chainId,
                    })}
                  />
                )}
              </EmptyDataWrap>
            </div>
            <div className="value">
              <ExternalLink href={getEtherscanLink(record.txHash, 'transaction')} className="history-time-with-icon">
                {formatDate(record.timestamp * 1000, 'MM-DD HH:mm')}
              </ExternalLink>
            </div>
          </>
        }>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.type')}</div>
          <div className="value">
            <span>
              {record.typeString === 'Remove'
                ? t(`common.historyC.liqHistory.Removed`)
                : t(`common.historyC.liqHistory.${record.typeString}`)}
            </span>
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.earn.priceRange')}</div>
          <div className="value">
            {priceMin.formatPriceNumberWithTooltip()} ~ {priceMax.formatPriceNumberWithTooltip()}
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.price')}</div>
          <div className="value">{WrappedBigNumber.from(record.fairPrice).formatNumberWithTooltip()}</div>
        </div>

        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.amount')}</div>
          <div className="value">{WrappedBigNumber.from(record.amount).formatNumberWithTooltip()}</div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.earn.feeEarning')}</div>
          <div className="value">
            <EmptyDataWrap isLoading={!record.feeEarned || WrappedBigNumber.from(record.feeEarned).isZero()}>
              {record.feeEarned &&
                WrappedBigNumber.from(record.feeEarned).formatNumberWithTooltip({
                  suffix: record?.quoteToken.symbol,
                })}
            </EmptyDataWrap>
          </div>
        </div>
      </MobileHistoryCard>
    );
  };

  return (
    <>
      <div className="syn-mobile-history-liquidity">
        {loading ? <MobileHistorySkeleton /> : histories.slice(0, 10).map(renderCard)}
      </div>
      {!loading && !!histories.length && <div className="syn-mobile-history-info">{t('mobile.history.tableInfo')}</div>}
      {!loading && !histories?.length && <Empty type="vertical" />}
    </>
  );
};

export default LiquidityHistory;
